import PropTypes from 'prop-types';

const IPHONE_5 = 320;
const IPHONE_6 = 375;
const IPAD_PORTRAIT = 768;
const IPAD_LANDSCAPE = 1024;
const DESKTOP = 1280;
const DESKTOP_XL = 1600;

export const MOBILE_RESOLUTIONS = [IPHONE_5, IPHONE_6, IPAD_PORTRAIT];

export const DESKTOP_RESOLUTIONS = [IPAD_LANDSCAPE, DESKTOP, DESKTOP_XL];

export const SUPPORTED_SCREEN_RESOLUTIONS = [
    IPHONE_5,
    IPHONE_6,
    IPAD_PORTRAIT,
    IPAD_LANDSCAPE,
    DESKTOP,
    DESKTOP_XL,
];

export const ACTION_TYPES = {
    PAGE_LOAD: '@@PAGE_LOAD',
};

export const BusinessUnit = {
    PATIENT_WEB_APP: 'PatientWebApp',
    GA_EVENT: 'GA-Event', // for legacy
};

export const EVENT_TYPES = {
    Pageview: 'Pageview',
    Action: 'Action',
    SetCustomDimension: 'Set custom dimension',
    SetContentGroup: 'Set content group',
    FullStoryUrl: 'FullStoryUrl',
    DidHideSyncOverlay: 'DidHideSyncOverlay',
    DidShowSyncOverlay: 'DidShowSyncOverlay',
};

export const EventDataSchemaVersion = {
    [BusinessUnit.PATIENT_WEB_APP]: {
        [EVENT_TYPES.Pageview]: '2.1.0',
        [EVENT_TYPES.Action]: '2.1.0',
        [EVENT_TYPES.SetCustomDimension]: '2.1.0',
        [EVENT_TYPES.SetContentGroup]: '2.1.0',
        [EVENT_TYPES.FullStoryUrl]: '2.0.0',
        [EVENT_TYPES.DidHideSyncOverlay]: '2.0.0',
        [EVENT_TYPES.DidShowSyncOverlay]: '2.0.0',
    },
};

export const providerShape = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    patientActive: PropTypes.bool,
    doctorAvailable: PropTypes.bool,

    title: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    circlePictureUrl: PropTypes.string,
    showPhotoWithCircleCss: PropTypes.bool,
    squarePictureUrl: PropTypes.string,
    averageRating: PropTypes.string,
    defaultProcedureId: PropTypes.string,
    profileUrl: PropTypes.string,
    mayChargeAdditionalCopay: PropTypes.bool,
    practice: PropTypes.shape({
        id: PropTypes.number,
        isUrgentCare: PropTypes.bool,
    }),
    relevantSpecialty: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
    }),
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            address1: PropTypes.string,
            address2: PropTypes.string,
            city: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
        })
    ),
    procedures: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
        })
    ),
});

export const procedureShape = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
});

export const specialtyShape = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    patientActive: PropTypes.bool,
    doctorAvailable: PropTypes.bool,
});

export const ProcedureIds = {
    ANNUAL_PHYSICAL: '76',
    DENTAL_CLEANING: '11',
    ILLNESS: '75',
    ORTHOPEDIC_CONSULTATION: '104',
    PLASTIC_SURGERY_CONSULTATION: '349',
    SKIN_SCREENING: '85',
    VISION_EXAM: '92',
};

export const SpecialtyIds = {
    ACUPUNCTURE_ORIENTAL_MEDICINE: '365',
    ADULT_PSYCHIATRIC_AND_MENTAL_HEALTH_NURSE_PRACTITIONER: '405',
    ALLERGIST: '132',
    CHILD_ADOLESCENT_PSYCHIATRIST: '250',
    CHILD_ADOLESCENT_PSYCHIATRIST_PEDIATRIC: '251',
    CHILD_ADOLESCENT_PSYCHOLOGIST: '416',
    DENTIST: '98',
    DERMATOLOGIST: '101',
    DIETITIAN: '359',
    EMERGENCY_MEDICINE_PHYSICIAN: '146',
    ENT: '130',
    EYE_DOCTOR: '386',
    FACIAL_PLASTIC_SURGEON: '167',
    FAMILY_PHYSICIAN: '102',
    FAMILY_PSYCHIATRIC_AND_MENTAL_HEALTH_NURSE_PRACTITIONER: '406',
    FORENSIC_PSYCHIATRIST: '280',
    GASTROENTEROLOGIST: '106',
    GENETICIST: '148',
    GERIATRIC_PSYCHIATRIST: '281',
    IMMUNOLOGIST: '154',
    INTERNIST: '99',
    MARRIAGE_FAMILY_THERAPIST: '438',
    MEDICAL_ETHICIST: '354',
    MENTAL_HEALTH_NURSE_PRACTITIONER: '407',
    MICROBIOLOGIST: '159',
    NATUROPATHIC: '373',
    NEUROPSYCHIATRIST: '383',
    OBGYN: '104',
    OPTOMETRIST: '157',
    ORTHOPEDIC_SURGEON: '117',
    PEDIATRIC_DENTIST: '152',
    PEDIATRICIAN: '100',
    PHYSICAL_THERAPIST: '335',
    PHYSICIAN_ASSISTANT: '381',
    PLASTIC_SURGEON: '120',
    PRIMARY_CARE_PHYSICIAN: '153',
    PSYCHIATRIST: '122',
    PSYCHIATRIST_PSYCHOLOGIST: '350',
    PSYCHOSOMATIC_MEDICINE_SPECIALIST: '299',
    PSYCHOTHERAPIST: '352',
    PSYCHOLOGIST: '337',
    ENDODONTIST: '134',
    ORTHODONTIST: '135',
    PERIODONTIST: '136',
    PROSTHODONTIST: '137',
    ORAL_SURGEON: '151',
    THERAPIST_COUNSELOR: '387',
    COVID_VACCINE_FACILITY: '510',
};

export const MENTAL_HEALTH_SPECIALTIES = new Set([
    SpecialtyIds.ADULT_PSYCHIATRIC_AND_MENTAL_HEALTH_NURSE_PRACTITIONER,
    SpecialtyIds.CHILD_ADOLESCENT_PSYCHIATRIST,
    SpecialtyIds.CHILD_ADOLESCENT_PSYCHIATRIST_PEDIATRIC,
    SpecialtyIds.CHILD_ADOLESCENT_PSYCHOLOGIST,
    SpecialtyIds.FAMILY_PSYCHIATRIC_AND_MENTAL_HEALTH_NURSE_PRACTITIONER,
    SpecialtyIds.FORENSIC_PSYCHIATRIST,
    SpecialtyIds.GERIATRIC_PSYCHIATRIST,
    SpecialtyIds.MARRIAGE_FAMILY_THERAPIST,
    SpecialtyIds.MENTAL_HEALTH_NURSE_PRACTITIONER,
    SpecialtyIds.NEUROPSYCHIATRIST,
    SpecialtyIds.PSYCHIATRIST,
    SpecialtyIds.PSYCHIATRIST_PSYCHOLOGIST,
    SpecialtyIds.PSYCHOLOGIST,
    SpecialtyIds.PSYCHOSOMATIC_MEDICINE_SPECIALIST,
    SpecialtyIds.PSYCHOTHERAPIST,
    SpecialtyIds.THERAPIST_COUNSELOR,
]);

export const VIRTUAL_VISIT_TYPES = {
    NONE: 'None',
    ZOCDOC_VIDEO_VISIT: 'ZocdocVideoVisit',
    THIRD_PARTY_VIDEO_VISIT: 'ThirdPartyVideoVisit',
};
